
import React, { useState, useMemo, useCallback, forwardRef } from 'react';
import { Row, Col, Spin, Form, Typography } from 'antd';
import { MODAL_SIZE_EXTRA_LARGE } from 'constants/Layout';
import _ from 'lodash';
import BaseModal from 'components/BaseModal';
import { FieldTypes } from "utils/formUtils";
import AppLoader from 'components/AppLoader';
import { generateFormFields } from "utils/formUtils";
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import { BASE_SEVERITIES } from 'views/Findings/Constants';
import { renderTime } from './FindingSLAConfigurationV2'
import moment from 'moment';
import message from 'components/CustomMessage';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';

const { Text } = Typography;

const getTimeInMs = (time, format = "days") => moment.duration(time, format).asMilliseconds().toFixed();
const HOUR = 60 * 60 * 1000;
export default forwardRef(function SLAForm({ slaId, data, afterClose = _.noop }, ref) {
  const [form] = Form.useForm();
  const editMode = !_.isUndefined(data);
  const disabledNameEdit = editMode && data.name === 'Default SLA';

  const [initState] = useState(()=> getInitValue(data, editMode));
  const { initValue } = initState;
  const severityField = (severity, type) => ({
    name: `${severity}_${type}`,
    label: getVocabulary(severity),
    type: FieldTypes.NUMBER,
    colSpan: 8,
    required: ["critical", "high"].indexOf(severity) !== -1,
    min: 1,
    rules: [({ getFieldValue }) => {
      return {
        validator: (rule, value) => {
          if(value){
            if(value < 1){
              return Promise.reject("SLA cannot be less than 1.");
            }
          }
          return Promise.resolve();
        }
      }
    }],
    props: {
      addonAfter: "Day(s)"
    }
  })

  const fields = useMemo(() => (
    [{
      name: "slaName",
      label: "Name",
      colSpan: 12,
      NoDefaultLengthRule: true,
      props: {
        autoFocus: true,
        disabled: disabledNameEdit
      },
      rules: [{
        required: true,
        message: <>Enter a SLA name.</>
      }],
    }, {
      name: "description",
      label: "Description",
      type: FieldTypes.TEXT_AREA,
      NoDefaultLengthRule: true,
      props: {
        disabled: disabledNameEdit,
      },
      rules: [{
        required: true,
        message: <>Enter a SLA description.</>
      }]
    },
    {
      type: FieldTypes.LABEL,
      value: <Text strong><Vocabulary name="triage"/> SLA</Text>,
      colSpan: 8
    },
    {
      type: FieldTypes.LABEL,
      value: <Text strong><Vocabulary name="remediation"/> SLA</Text>,
      colSpan: 8
    },
    {
      type: FieldTypes.LABEL,
      value: <Text strong><Vocabulary name="resolution"/> SLA</Text>,
      colSpan: 8
    },
    severityField('critical', 'firstResponseSla'),
    severityField('critical', 'developerSla'),
    severityField('critical', 'resolutionSla'),
    severityField('high', 'firstResponseSla'),
    severityField('high', 'developerSla'),
    severityField('high', 'resolutionSla'),
    severityField('medium', 'firstResponseSla'),
    severityField('medium', 'developerSla'),
    severityField('medium', 'resolutionSla'),
    severityField('low', 'firstResponseSla'),
    severityField('low', 'developerSla'),
    severityField('low', 'resolutionSla'),
    ]
  ), []) //eslint-disable-line

  const onSubmit = useCallback(async () => {
    await form.validateFields();
    const data = form.getFieldValue();
    const formData = modifiedData(data);
    if(editMode && slaId){
      await GlobalSettingAPI.updateSLA(slaId, formData).promise;
    }else{
      await GlobalSettingAPI.createSLA(formData).promise;
    }
    message.success(
      <>{(editMode && slaId) ? "Edited" : "Created" } {data.slaName}</>
  );
  }, [form, slaId, editMode])

  const baseModalProps = {
    title: (editMode && slaId) ? 'Edit SLA' : 'Add SLA',
    width: MODAL_SIZE_EXTRA_LARGE,
    okText: 'Save',
    onOk: onSubmit
  };
  return (
    <BaseModal
      ref={ref}
      modalProps={baseModalProps}
      afterClose={afterClose}
    >
      <Spin spinning={false} indicator={<AppLoader style={{ marginTop: 0 }} />}>
        <Row><Col span={24}>Create SLA with desired name and description based on your configuration you would like.</Col></Row>
        <Form
          layout="vertical"
          initialValues={initValue}
          form={form}
        >
          {generateFormFields(fields)}
        </Form>
      </Spin>
    </BaseModal>
  );
});


function getInitValue(data, editMode = true) {
  let initValue = {}
  let initFormat = {}
  BASE_SEVERITIES.forEach(severity => {
    const key = _.toLower(severity);
    initValue[`${key}_firstResponseSla`] = renderTime(HOUR);
    initValue[`${key}_resolutionSla`] = renderTime(HOUR);
    initValue[`${key}_developerSla`] = renderTime(HOUR);
  })

  if (editMode) {
    const { name: slaName, description, sla } = data;
    initValue = { ...initValue, slaName, description }
    sla.forEach(slaData => {
      const key = _.toLower(slaData.severity)
      initValue[`${key}_firstResponseSla`] = renderTime(slaData.firstResponseSla);
      initValue[`${key}_resolutionSla`] = renderTime(slaData.resolutionSla);
      initValue[`${key}_developerSla`] = renderTime(slaData.developerSla);
    })
  }
  return { initValue, initFormat }
}

function modifiedData(data) {
  const { slaName: name, description } = data;
  const sla = [];
  const tempObj = {}
  const inputData = { ...data }
  _.forEach(inputData, (value, key)=>{
    const slaKeys = key.split`_`
    const severity = slaKeys[0];
    if (BASE_SEVERITIES.indexOf(_.capitalize(severity)) !== -1) {
      const slaProperty = slaKeys[1];
      const slaFormat = slaKeys[2];
      const key = !_.isUndefined(slaFormat) ? `${slaProperty}_${slaFormat}` : slaProperty;
      tempObj[severity] = { ...tempObj[severity], [key]: value };
    }
  })
  
  for (const severity of BASE_SEVERITIES) {
    const currentSeveritySlaData = tempObj[_.lowerCase(severity)];
    const { firstResponseSla, resolutionSla, developerSla } = currentSeveritySlaData
    sla.push({
      severity,
      firstResponseSla: getTimeInMs(firstResponseSla),
      resolutionSla: getTimeInMs(resolutionSla),
      developerSla: getTimeInMs(developerSla)
    })
  }
  return {
    name,
    description,
    sla
  }
}